import React, { useState, useEffect, Fragment } from "react";
import { Card, notification, Spin, Button, Descriptions, Empty } from "antd";
import { DownloadOutlined, EyeOutlined, ArrowLeftOutlined, LinkOutlined, FileTextOutlined } from "@ant-design/icons";
import { withRouter } from "react-router";
import { connect } from "react-redux";
import Breadcrumb from "../common/breadcrumb";
import { callGetApiToServer } from "../../Graphs/callApi";
import { WEBURL } from "../../constant/comman";
import moment from "moment";

const AssignmentSubmissionReview = (props) => {
  const { authToken, match, history } = props;
  const { submissionId, questionId } = match.params;

  const [detail, setDetail] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchDetails = async () => {
    setIsLoading(true);
    try {
      const header = {
        Accept: "application/json",
        "Content-Type": "application/json",
        authorization: "Bearer " + authToken,
      };

      const endUrl = `v1/auth/admin/assignment-submissions/submissions/${submissionId}/question/${questionId}`;
      const res = await callGetApiToServer(header, "GET", endUrl);

      if (res.status === 200) {
        setDetail(res.data);
      } else {
        notification.error({
          message: "Error",
          description: res.message || "Failed to load submission details",
        });
      }
    } catch (error) {
      console.error("Error fetching submission details:", error);
      notification.error({
        message: "Error",
        description: "Something went wrong while fetching submission details.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDetails();
  }, [authToken, submissionId, questionId]);

  const getFileUrl = (fileName, isSubmission = true) => {
    if (!fileName) return "";
    if (fileName.startsWith("http://") || fileName.startsWith("https://")) {
      return fileName;
    }
    const baseUrl = WEBURL.endsWith("/") ? WEBURL : WEBURL + "/";
    // Student uploaded documents are inside course/topic/question/answer/
    // Question resource documents might be stored elsewhere or inside the same. Let's see: we can serve from base course uploads or standard path.
    // If it's a submission we look in answers, if it's original question we check course/topic/question/
    const path = isSubmission ? "course/topic/question/answer/" : "course/topic/question/";
    return `${baseUrl}${path}${fileName}`;
  };

  if (isLoading) {
    return (
      <Fragment>
        <Breadcrumb title="Submission Review" parent="Assignment Submissions" child="Review" />
        <div className="d-flex justify-content-center align-items-center" style={{ height: "400px" }}>
          <Spin size="large" tip="Loading submission details..." />
        </div>
      </Fragment>
    );
  }

  if (!detail) {
    return (
      <Fragment>
        <Breadcrumb title="Submission Review" parent="Assignment Submissions" child="Review" />
        <div className="container-fluid">
          <Card>
            <Empty description="No submission details found." />
            <div className="text-center mt-3">
              <Button type="primary" icon={<ArrowLeftOutlined />} onClick={() => history.goBack()}>
                Go Back
              </Button>
            </div>
          </Card>
        </div>
      </Fragment>
    );
  }

  const { student, course, quiz, question, submission } = detail;
  const submissionFileUrl = getFileUrl(submission?.uploaded_document, true);
  const questionFileUrl = question?.open_book_file ? getFileUrl(question.open_book_file, false) : "";

  return (
    <Fragment>
      <Breadcrumb title="Submission Review" parent="Assignment Submissions" child="Review" />
      <div className="container-fluid">
        <div className="mb-4">
          <Button type="default" icon={<ArrowLeftOutlined />} onClick={() => history.goBack()}>
            Back to Submissions
          </Button>
        </div>

        <div className="row">
          <div className="col-xl-6 col-md-12">
            <Card title="Student & Course Information" className="mb-4 shadow-sm">
              <Descriptions bordered column={1} size="small">
                <Descriptions.Item label="Student Name">
                  <strong>{student?.name || "N/A"}</strong>
                </Descriptions.Item>
                <Descriptions.Item label="Student Email">
                  {student?.email || "N/A"}
                </Descriptions.Item>
                <Descriptions.Item label="Student ID">
                  {student?.id || "N/A"}
                </Descriptions.Item>
                <Descriptions.Item label="Course Name">
                  {course?.name || "N/A"}
                </Descriptions.Item>
                <Descriptions.Item label="Quiz / Assignment Topic">
                  {quiz?.name || "N/A"}
                </Descriptions.Item>
                <Descriptions.Item label="Submission Date & Time">
                  {submission?.submission_date
                    ? moment(submission.submission_date).format("DD-MM-YYYY HH:mm:ss")
                    : "N/A"}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <Card title="Original Question Details" className="mb-4 shadow-sm">
              <div className="mb-3">
                <h6>Question Title:</h6>
                <p style={{ fontSize: "15px", fontWeight: "600", color: "#333" }}>{question?.title || "N/A"}</p>
              </div>
              <div className="mb-3">
                <h6>Question Description / Instruction:</h6>
                <div
                  style={{
                    backgroundColor: "#f9f9f9",
                    padding: "12px",
                    borderRadius: "6px",
                    border: "1px solid #eee",
                    minHeight: "80px",
                    whiteSpace: "pre-wrap"
                  }}
                >
                  {question?.description || "No description provided."}
                </div>
              </div>

              <div className="mb-3">
                <h6>Original Assignment Resources:</h6>
                <div className="d-flex flex-column" style={{ gap: "10px" }}>
                  {question?.open_book_file ? (
                    <div className="d-flex align-items-center justify-content-between p-2" style={{ backgroundColor: "#f0f2f5", borderRadius: "4px" }}>
                      <span>
                        <FileTextOutlined style={{ marginRight: "8px", color: "#1890ff" }} />
                        {question.open_book_file}
                      </span>
                      <a href={questionFileUrl} target="_blank" rel="noopener noreferrer" download>
                        <Button type="primary" size="small" icon={<DownloadOutlined />}>
                          Download File
                        </Button>
                      </a>
                    </div>
                  ) : (
                    <span className="text-muted">No resource document attached by Admin.</span>
                  )}

                  {question?.open_book_link ? (
                    <div className="d-flex align-items-center justify-content-between p-2" style={{ backgroundColor: "#f0f2f5", borderRadius: "4px" }}>
                      <span>
                        <LinkOutlined style={{ marginRight: "8px", color: "#52c41a" }} />
                        External Reference Link
                      </span>
                      <a href={question.open_book_link} target="_blank" rel="noopener noreferrer">
                        <Button type="default" size="small" icon={<EyeOutlined />}>
                          Visit Link
                        </Button>
                      </a>
                    </div>
                  ) : null}
                </div>
              </div>
            </Card>
          </div>

          <div className="col-xl-6 col-md-12">
            <Card title="Student's Answer Upload" className="mb-4 shadow-sm" style={{ minHeight: "380px" }}>
              {submission?.uploaded_document ? (
                <div className="text-center p-4">
                  <div className="mb-4">
                    <FileTextOutlined style={{ fontSize: "64px", color: "#52c41a" }} />
                    <h5 className="mt-3">Submitted Document</h5>
                    <p className="text-muted">{submission.uploaded_document}</p>
                  </div>

                  <div className="d-flex justify-content-center" style={{ gap: "15px" }}>
                    <a href={submissionFileUrl} target="_blank" rel="noopener noreferrer">
                      <Button type="primary" size="large" icon={<EyeOutlined />}>
                        View Document
                      </Button>
                    </a>
                    <a href={submissionFileUrl} download target="_blank" rel="noopener noreferrer">
                      <Button type="default" size="large" icon={<DownloadOutlined />}>
                        Download Document
                      </Button>
                    </a>
                  </div>

                  {/* PDF Viewer preview if file type is PDF */}
                  {submission.uploaded_document.toLowerCase().endsWith(".pdf") && (
                    <div className="mt-4" style={{ border: "1px solid #ccc", borderRadius: "4px", overflow: "hidden" }}>
                      <iframe
                        src={submissionFileUrl}
                        title="Submission Document Preview"
                        width="100%"
                        height="400px"
                        style={{ border: "none" }}
                      />
                    </div>
                  )}
                </div>
              ) : (
                <Empty description="No answer document has been uploaded by the student." />
              )}
            </Card>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

const mapStateToProps = (state) => ({
  authToken: state.auth.authToken,
});

export default connect(mapStateToProps)(withRouter(AssignmentSubmissionReview));
